import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopApp, isDesktopOffline } from '@/api/desktopSync';
import { localBackendUrl } from '@/api/localBackend';
import { submitManualOrder } from '@/api/orders';
import { addressReviewFrom, addressAcceptance } from '@/utils/addressConfirmation';
import { createIdempotencyKey } from '@/utils/idempotency';
import { ensureManualOrderRecord, getManualOrderRecord, getManualOrdersRevision,
  manualAcceptanceMessage, manualFailure, manualOrderPrincipalKey,
  replaceManualOrderRecord, subscribeManualOrders } from '@/utils/manualOrderAttempts';

export default function useManualOrder(makeEmptyForm, onOrderCreated) {
  const { sessionBinding } = useAuth();
  const sessionRevision = useSyncExternalStore(subscribeActiveSessionRevision,
    getActiveSessionRevision, getActiveSessionRevision);
  useSyncExternalStore(subscribeManualOrders, getManualOrdersRevision, getManualOrdersRevision);
  const principalKey = manualOrderPrincipalKey(sessionBinding);
  const coherent = Boolean(principalKey && sessionBinding.revision === sessionRevision);
  const record = coherent ? ensureManualOrderRecord(principalKey, makeEmptyForm()) : null;
  const live = useRef(null);
  live.current = { principalKey, revision: sessionRevision, coherent };
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const current = () => Boolean(mounted.current && coherent && live.current.coherent
    && live.current.principalKey === principalKey && live.current.revision === sessionRevision
    && getActiveSessionRevision() === sessionRevision);
  const edit = update => {
    if (!current()) return;
    const previous = getManualOrderRecord(principalKey);
    if (['pending', 'uncertain'].includes(previous?.status)) return;
    const fields = typeof update === 'function' ? update(previous.fields) : update;
    replaceManualOrderRecord(previous, { fields, status: 'draft', attempt: null,
      receipt: null, difference: null, addressReview: null, message: '' });
  };
  const publish = async accepted => {
    if (!current() || !accepted?.receipt) return;
    const owner = { principalKey, revision: sessionRevision, isCurrent: current };
    try {
      await onOrderCreated?.(accepted.receipt.order, owner);
    } catch {
      // Acceptance is already retained; a screen failure cannot turn it into
      // a failed create or unlock re-submission of the accepted operation.
      replaceManualOrderRecord(accepted, {
        message: `${manualAcceptanceMessage(accepted.receipt)} The recent-order display could not be updated. Use Show saved order to try that display step again.`,
      });
    }
  };
  const submit = async ({ acceptCanonicalAddress = false } = {}) => {
    if (!current()) return;
    const previous = getManualOrderRecord(principalKey);
    if (!previous || previous.status === 'pending' || previous.status === 'accepted') return;
    const retry = previous.status === 'uncertain';
    if (acceptCanonicalAddress && previous.addressReview?.sessionRevision !== sessionRevision) {
      replaceManualOrderRecord(previous, { status: 'draft', difference: null, addressReview: null,
        attempt: null, message: 'Your sign-in changed. Entries are retained; submit again to review the current address suggestion.' });
      return;
    }
    const acceptance = acceptCanonicalAddress ? addressAcceptance(previous.addressReview) : null;
    if (acceptCanonicalAddress && !acceptance) return;
    const required = ['delivery_date', 'recipient_name', 'recipient_street_address', 'recipient_zip_code', 'product_description', 'total_price'];
    if (!retry && required.some(field => !previous.fields[field])) {
      replaceManualOrderRecord(previous, { message: 'Please fill in all required fields: Delivery Date, Recipient Name, Street Address, Zip Code, Product Description, and Total Price.' });
      return;
    }
    const offline = isDesktopOffline();
    const desktopApp = isDesktopApp();
    if (acceptCanonicalAddress && (previous.attempt?.offline !== offline || previous.attempt?.desktopApp !== desktopApp)) {
      replaceManualOrderRecord(previous, { status: 'draft', difference: null, addressReview: null,
        attempt: null, message: 'Connection mode changed. Entries are retained; submit again to check the address in this mode.' });
      return;
    }
    const attempt = retry ? previous.attempt : {
      key: previous.status === 'address' ? previous.attempt.key : createIdempotencyKey('manual-order'),
      payload: { ...previous.fields, ...acceptance },
      offline, desktopApp,
    };
    if (attempt.offline !== offline || attempt.desktopApp !== desktopApp) {
      replaceManualOrderRecord(previous, { message: 'Return to the original connection mode before retrying this submission.' });
      return;
    }
    const cloudToken = !offline && getAuthTokenForRequest(`${API}/orders`);
    const localUrl = localBackendUrl();
    const localToken = desktopApp && localUrl && getAuthTokenForRequest(`${localUrl}/api/desktop/orders`);
    if (!(offline ? localToken : cloudToken)) {
      replaceManualOrderRecord(previous, { message: 'Sign in again to the original account before submitting.' });
      return;
    }
    const pending = replaceManualOrderRecord(previous, { status: 'pending', attempt,
      message: 'Submitting this order…', difference: null });
    if (!pending) return;
    // Dispatch ownership is independent of view mount: navigating may retain a
    // request, but session replacement must prevent any secondary local write.
    const ownsSession = () => getActiveSessionRevision() === sessionRevision
      && isDesktopOffline() === offline && isDesktopApp() === desktopApp;
    try {
      const result = await submitManualOrder(pending.attempt.payload, pending.attempt.key, {
        offline, desktopApp, isCurrent: ownsSession, recoveryTarget: pending.attempt.recoveryTarget,
        requestConfig: { headers: { Authorization: cloudToken ? `Bearer ${cloudToken}` : '' } },
        localRequestConfig: { headers: { Authorization: localToken ? `Bearer ${localToken}` : '' } },
      });
      if (!result?.order?.id || !['created_in_cloud', 'saved_locally', 'submission_uncertain'].includes(result.outcome)) {
        throw new Error('No usable order receipt');
      }
      const accepted = replaceManualOrderRecord(pending, { status: 'accepted', receipt: result,
        fields: makeEmptyForm(), message: manualAcceptanceMessage(result), difference: null });
      await publish(accepted);
    } catch (error) {
      const addressReview = addressReviewFrom(error);
      const difference = addressReview?.difference;
      // Cloud address refusal follows same-key receipt lookup and precedes
      // acceptance. Even after uncertainty this specific refusal proves that
      // canonical consent is still needed; ordinary validation does not.
      if (difference && (!retry || (!pending.attempt.offline
          && pending.attempt.recoveryTarget !== 'local_uncertain'))) {
        replaceManualOrderRecord(pending, { status: 'address', difference,
          addressReview: { ...addressReview, sessionRevision },
          message: 'Nothing has been saved. Confirm the suggested address or edit the form.' });
      } else {
        replaceManualOrderRecord(pending, { ...manualFailure(error, retry), difference: null,
          attempt: error?.localPreservation === 'failed'
            ? { ...pending.attempt, recoveryTarget: 'local_uncertain' } : pending.attempt });
      }
    }
  };
  return { record, fields: record?.fields || makeEmptyForm(), edit, submit,
    publish: () => publish(getManualOrderRecord(principalKey)),
    dismissAddress: () => { if (current()) replaceManualOrderRecord(record, { status: 'draft', difference: null, addressReview: null, attempt: null }); },
    locked: !coherent || ['pending', 'uncertain'].includes(record?.status),
    pending: record?.status === 'pending', current };
}
